import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { Search, X } from 'lucide-react';
import { useAppTheme } from '@/utils/theme-context';

// Shared timing for open + close so the collapse animation exactly mirrors
// the expand animation (same duration/easing, defined per-variant below).
const TRANSITION = { duration: 0.18, ease: 'easeOut' as const };

// Matches the inactive category pill's rendered height in CategoryChipsRow
// (padding: '5px 14px' + 12px/500 'Syne' text ≈ 24-26px tall).
const COLLAPSED_SIZE = 26;
const EXPANDED_WIDTH = 172;

interface CollapsibleSearchProps {
  /** Current search query value. */
  value: string;
  /** Called on every keystroke, and with '' when the search is closed. */
  onChange: (value: string) => void;
  placeholder?: string;
  /** Open/closed state — lifted to the parent since it also drives a results overlay. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional ref to the underlying <input>, so a parent can imperatively refocus it. */
  inputRef?: React.RefObject<HTMLInputElement | null>;
}

/**
 * Collapsed = a circular magnifier icon button styled exactly like the
 * neighbouring (inactive) category pills. Tapping it slides a search input
 * open in its place; tapping the icon again or the trailing X collapses it
 * back and clears the query. Used as the leading item in every Explore
 * category chip row (Wallpapers / Live / Ringtones / By Users all share one
 * instance) so all sections behave identically.
 */
export const CollapsibleSearch: React.FC<CollapsibleSearchProps> = ({
  value,
  onChange,
  placeholder = 'Search...',
  open,
  onOpenChange,
  inputRef,
}) => {
  const { tokens } = useAppTheme();
  const localRef = useRef<HTMLInputElement>(null);
  const ref = inputRef ?? localRef;

  const handleClose = () => {
    onChange('');
    onOpenChange(false);
  };

  const handleToggle = () => {
    if (open) {
      handleClose();
    } else {
      onOpenChange(true);
    }
  };

  return (
    <motion.div
      initial={false}
      animate={
        open
          ? { width: EXPANDED_WIDTH, transition: TRANSITION }
          : { width: COLLAPSED_SIZE, transition: TRANSITION }
      }
      style={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        height: COLLAPSED_SIZE,
        padding: open ? '0 10px' : 0,
        borderRadius: 999,
        border: `1px solid ${open ? 'rgba(70,245,224,0.5)' : 'rgba(255,255,255,0.13)'}`,
        background: 'transparent',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={handleToggle}
        aria-label={open ? 'Close search' : 'Search'}
        style={{
          flexShrink: 0,
          width: 16,
          height: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          margin: open ? 0 : 'auto',
        }}
      >
        <Search size={13} color="rgba(240,240,245,0.55)" strokeWidth={2} />
      </button>
      {open && (
        <>
          <input
            ref={ref}
            type="text"
            className="seekscape-search-input"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            autoFocus
            style={{
              flex: 1,
              minWidth: 0,
              background: 'none',
              border: 'none',
              outline: 'none',
              color: tokens.textPrimary,
              fontSize: 12,
              fontWeight: 500,
              fontFamily: "'Syne', sans-serif",
              letterSpacing: '0.01em',
            }}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
          />
          <button
            type="button"
            onClick={handleClose}
            aria-label="Clear search"
            style={{
              width: 14,
              height: 14,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              color: 'rgba(240,240,245,0.5)',
              flexShrink: 0,
            }}
          >
            <X size={12} strokeWidth={2.5} />
          </button>
        </>
      )}
    </motion.div>
  );
};

export default CollapsibleSearch;
